import { BehaviorSubject, defer, Observable, Subscription } from "rxjs";
import { promiseFirstDefined } from "./promise";

interface LoadingSubjectValue{
	[key:string]:any;
	dispose?:()=>void;
	save?():Promise<boolean>;
}

/** @deprecated use LoadingSubjectPromise */
export abstract class LoadingSubject<T extends LoadingSubjectValue, LoadArgs extends any[]=[]> extends BehaviorSubject<T|null|undefined>{
	protected sub:Subscription|undefined;

	public constructor(){
		super(undefined);
	}

	private disposeValue(){
		const value=this.getValue();
		if(value){
			if(Object.hasOwn(value,'save') && typeof(value.save)==='function')
				delete value.save;
			if(typeof(value.dispose)==='function')
				value.dispose();
		}
	}

	protected saver():((value:T)=>Promise<boolean>)|undefined{
		//override me if you want to give your values a save function
		return undefined;
	}

	public override complete(){
		this.disposeValue();
		this.sub?.unsubscribe();
		super.complete();
	}

	public abstract load$(...args:LoadArgs):Observable<T|null>;

	public load(...args:LoadArgs):Promise<T|null>{
		const load$=this.load$(...args);
		return promiseFirstDefined(load$);
	}

	public loaded(){
		return this.value!==undefined;
	}

	public exists(){
		if(this.value===undefined)
			return undefined;
		return !!this.value;
	}

	public override next(value:T|null|undefined){
		this.disposeValue();
		if(value){
			const saveFunc=this.saver();
			if(saveFunc){
				value.save=function(){
					return saveFunc(this);
				};
			}
		}
		super.next(value);
	}

	public reload(...args:LoadArgs){
		this.disposeValue();
		this.sub?.unsubscribe();
		this.next(undefined);

		return this.load(...args);
	}
}

export abstract class LoadingSubjectPromise<T extends LoadingSubjectValue, LoadArgs extends any[]=[]> extends BehaviorSubject<T|null|undefined>{
	private promise:Promise<T|null>|null=null;

	public constructor(){
		super(undefined);
	}

	private disposeValue(){
		const value=this.getValue();
		if(value){
			if(Object.hasOwn(value,'save') && typeof(value.save)==='function')
				delete value.save;
			if(typeof(value.dispose)==='function')
				value.dispose();
		}
	}

	protected saver():((value:T)=>Promise<boolean>)|undefined{
		//override me if you want to give your values a save function
		return undefined;
	}

	public override complete(){
		this.promise=null;
		this.disposeValue();
		super.complete();
	}

	public loaded(){
		return this.value!==undefined;
	}

	public exists(){
		if(this.value===undefined)
			return undefined;
		return !!this.value;
	}

	public override next(value:T|null|undefined){
		this.disposeValue();
		if(this.value!==value){
			if(value!==undefined)
				this.promise=Promise.resolve(value);
			else
				this.promise=null;
		}
		if(value){
			const saveFunc=this.saver();
			if(saveFunc){
				value.save=function(){
					return saveFunc(this);
				};
			}
		}
		super.next(value);
	}

	protected abstract _load(...args:LoadArgs):Promise<T>;

	private getPromise(args:LoadArgs){
		if(!this.promise){ //if not already loading
			this.promise=this._load(...args).then(v=>{
				this.next(v);
				return v;
			});
		}
		return this.promise;
	}

	public load$(...args:LoadArgs){
		if(this.value!==undefined)
			return this;//already loaded

		this.getPromise(args);
		return this;
	}

	public load(...args:LoadArgs){
		return this.getPromise(args);
	}

	public loadOnSub$(...args:LoadArgs){
		return defer(()=>{
			return this.getPromise(args);
		});
	}

	public unload(){
		this.promise=null;
		this.disposeValue();
		this.next(undefined);
	}
}

